import React, {
    memo, useContext, useState, useEffect,
    useMemo
} from 'react'
import { Link } from 'react-router-dom'
import { FiBell } from 'react-icons/fi'
import * as icons from 'react-icons/fa'
import { FaStickyNote } from 'react-icons/fa'
import { addHours, format, isBefore } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { emerald, yellow, blue, gray } from 'tailwindcss/colors'

import { Button } from '../Form'
import Modal from '../Modal'

import { GlobalContext } from '../../contexts/GlobalContext'

import {
    Container, Content, Profile, AreaNotificacoes, Avatar, Dropdown, RecadoContainer
} from './styles'

import avatarDefault from '../../assets/images/avatar.png'
import { api, authHeaders } from '../../services/api'
import stringUtils from '../../util/string'
import { notify } from '../../config/notification'

const profileNameMaxLength = 13

function obterCorIcone(item) {
    if(item.visto) {
        return gray[400]
    }

    switch(item.icone) {
        case 'FaCheck': 
            return emerald[500]
        case 'FaExclamationTriangle': 
            return yellow[500]
        default: 
            return blue[500]
    }
}

const Header = memo(({ usuario }) => {
    const { user, clienteRobot, handleLogout, checkException } = useContext(GlobalContext)

    const [listaNotificacoesVisible, setListaNotificacoesVisible] = useState(false)
    const [closeNotificacoesListaOnClickOutside, setCloseNotificacoesListaOnClickOutside] = useState(false)

    const [notificacoes, setNotificacoes] = useState([])
    const [quantidadeNotificacoesNaoLidas, setQuantidadeNotificacoesNaoLidas] = useState(0)
    const [notificacoesVerMaisVisible, setNotificacoesVerMaisVisible] = useState(false)
    const [openNotificacao, setOpenNotificacao] = useState(null)

    async function loadNotificacoes() {
        try {
            const response = await api.get('notificacao', authHeaders())

            setNotificacoes(old => {
                const wasNewNotificationAlertShown = !!sessionStorage.getItem('sigas-project-alert-notification-control')
                const naoVistosAnterior = old.filter(r => !r.visto)
                const naoVistosNovo = response.recados.filter(r => !r.visto)

                if(!wasNewNotificationAlertShown && naoVistosAnterior.length < naoVistosNovo.length) {
                    notify({
                        title: 'Nova notificação',
                        content: 'Existem novas notificações para você! Clique aqui para visualizar.',
                        onClick: e => {
                            e.preventDefault()

                            setListaNotificacoesVisible(true)
                        }
                    })

                    const datetimeToDeleteNotificationControlAlert = addHours(new Date(), 1)

                    sessionStorage.setItem('sigas-project-alert-notification-control', datetimeToDeleteNotificationControlAlert.toString())
                }
                
                return response.recados.map(recado => ({
                    ...recado,
                    remetente: {
                        id: recado.remetente?.id || 1,
                        nome: recado.remetente?.nome ? stringUtils.reduceName(recado.remetente.nome) : 'Syndi'
                    },
                    destinatario: {
                        id: recado.destinatario.id,
                        nome: stringUtils.reduceName(recado.destinatario.nome)
                    },
                    mensagem: recado.mensagem || recado.tipo.mensagem,
                    icone: recado.tipo.icone
                }))
            })

            setQuantidadeNotificacoesNaoLidas(response.nao_lidos)

            setNotificacoesVerMaisVisible(response.total_recados > response.recados.length)
        } catch (e) {
            checkException(e, { logout: false })
        }
    }

    async function loadNotificacoesMaisAntigas() {
        try {
            const response = await api.get('notificacao', {
                params: {
                    recados_visiveis: notificacoes.length
                },
                ...authHeaders()
            })

            setNotificacoes(response.recados.map(recado => ({
                ...recado,
                remetente: {
                    id: recado.remetente?.id || 1,
                    nome: recado.remetente?.nome ? stringUtils.reduceName(recado.remetente.nome) : 'Syndi'
                },
                destinatario: {
                    id: recado.destinatario.id,
                    nome: stringUtils.reduceName(recado.destinatario.nome)
                },
                mensagem: recado.mensagem || recado.tipo.mensagem,
                icone: recado.tipo.icone
            })))

            setQuantidadeNotificacoesNaoLidas(response.nao_lidos)

            setNotificacoesVerMaisVisible(response.total_recados > response.recados.length)
        } catch (e) {
            checkException(e)
        }
    }

    async function handleLerRecado(recado) {
        setOpenNotificacao(recado)

        try {
            await api.patch(`recepcao/recado/${recado.id}`, {}, authHeaders())

            const currentRecado = notificacoes.find(item => item.id === recado.id)

            currentRecado.visto = true

            const posicao = notificacoes.map(item => item.id).indexOf(recado.id)

            setNotificacoes(old => [
                ...old.slice(0, posicao),
                currentRecado,
                ...old.slice(posicao + 1)
            ])

            setQuantidadeNotificacoesNaoLidas(old => old > 0 ? old - 1 : 0)
        } catch (e) {
            checkException(e)
        }
    }

    useEffect(() => {
        if (listaNotificacoesVisible && !closeNotificacoesListaOnClickOutside) {
            setCloseNotificacoesListaOnClickOutside(true)

            document.addEventListener('click', e => {
                const trigger = document.querySelector('.lista-avisos-trigger')
                const lista = document.querySelector('.recados-lista')
                const modal = document.querySelector('.ReactModal__Overlay')

                if (!lista?.contains(e.target) && !trigger?.contains(e.target) && !modal?.contains(e.target)) {
                    setListaNotificacoesVisible(false)
                }
            })
        }
    }, [listaNotificacoesVisible, closeNotificacoesListaOnClickOutside])

    useEffect(() => {
        if (user) {
            loadNotificacoes()

            const timerNotificacao = process.env.REACT_APP_ENVIRONMENT === 'development' ? 5000 : 2 * 60 * 1000

            const interval = setInterval(loadNotificacoes, timerNotificacao)

            return () => {
                clearInterval(interval)
            }
        }
    }, [user])

    useEffect(() => {
        if(user) {
            setInterval(() => {
                const notificationAlert = sessionStorage.getItem('sigas-project-alert-notification-control')

                if(notificationAlert) {
                    const datetime = new Date(notificationAlert)

                    if(isBefore(datetime, new Date())) {
                        sessionStorage.removeItem('sigas-project-alert-notification-control')
                    }
                }
            })
        }
    }, [user])

    const foto = user?.avatar || user?.pessoa.documentos?.find(doc => doc.tipo_documento_id === 15)?.arquivo?.link

    return (
        <>
            <Container>
                <Content>
                    <div>
                        <Link to="/dashboard">
                            <img src={clienteRobot?.logo} alt="Home" className="object-contain" />
                        </Link>
                    </div>

                    <aside>
                        <AreaNotificacoes onClick={() => { setListaNotificacoesVisible(old => !old) }}>
                            <FiBell size={28} />

                            {quantidadeNotificacoesNaoLidas > 0 && (
                                <div className="badge">
                                    <span>{quantidadeNotificacoesNaoLidas}</span>
                                </div>
                            )}
                        </AreaNotificacoes>

                        {listaNotificacoesVisible && (
                            <Dropdown>
                                {notificacoes.map((recado, index) => (
                                    <div
                                        key={recado.id}
                                        className={`item animated fadeIn fast delay-${100 * index}ms`}
                                        onClick={() => { handleLerRecado(recado) }}
                                        role="button"
                                        tabIndex={0}
                                    >
                                        {React.createElement(recado.icone ? icons[recado.icone] : FaStickyNote, {
                                            size: 20,
                                            color: obterCorIcone(recado) // .visto ? '#777' : '#4caf50'
                                        })}

                                        <b>{recado.remetente?.nome || 'Sistema'}</b>

                                        <span className="info" dangerouslySetInnerHTML={{ __html: recado.mensagem || recado.tipo.descricao }} />

                                        <span className="datetime">{format(new Date(recado.created_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })}</span>
                                    </div>
                                ))}

                                {notificacoesVerMaisVisible && (
                                    <div
                                        className="item ver-mais"
                                        onClick={loadNotificacoesMaisAntigas}
                                        role="button"
                                        tabIndex={0}
                                    >
                                        <span>Ver notificações mais antigas</span>
                                    </div>
                                )}
                            </Dropdown>
                        )}

                        <Profile>
                            <div>
                                <strong>{usuario?.pessoa.nome.length > profileNameMaxLength ? `${usuario?.pessoa.nome.substr(0, profileNameMaxLength)}...` : usuario?.pessoa.nome}</strong>

                                <Button onClick={handleLogout} className="transparent" id="logout-button">
                                    Sair
                                </Button>
                            </div>

                            <Link to="/perfil">
                                <Avatar image={foto || avatarDefault} />
                            </Link>
                        </Profile>
                    </aside>

                </Content>
            </Container>

            <Modal
                isOpen={!!openNotificacao}
                handleClose={() => { setOpenNotificacao(null) }}
                title={`Nova mensagem${openNotificacao?.remetente?.nome ? ` de ${openNotificacao?.remetente?.nome}` : ''}`}
            >
                {openNotificacao ? (
                    <RecadoContainer>
                        <div dangerouslySetInnerHTML={{ __html: `Data e hora do recado: <b>${format(new Date(openNotificacao?.created_at), 'dd/MM/yyyy HH:mm\'h\'')}</b>` }} />

                        <div className="message-container" dangerouslySetInnerHTML={{ __html: openNotificacao?.mensagem }} />

                        <div className="button-container">
                            {openNotificacao?.link && (
                                <a href={openNotificacao.link} className="button transparent">
                                    Acessar
                                </a>
                            )}
                        </div>
                    </RecadoContainer>
                ) : null}
            </Modal>
        </>
    )
})

export default Header
